import { startSpan } from "@sentry/react";
import { captureException } from "./index";

/**
 * The controlled values that incident triage may group on.  Callers must never
 * turn a provider response, an operator input, or an identifier into one of
 * these values.
 */
export type SentryErrorCode =
  | "access_denied"
  | "backend_update_required"
  | "cancelled"
  | "not_configured"
  | "persistence_failed"
  | "provider_unavailable"
  | "request_failed"
  | "session_required"
  | "stream_incomplete"
  | "follow_up_failed";

export type SentryFailurePhase = "request" | "stream" | "persistence" | "post_commit";

export type SentryOperation = "assistant.text.request" | "purchase-order.create";

interface OperationInput {
  model?: string;
  operation: SentryOperation;
  provider?: string;
  requestId: string;
}

interface FailureInput {
  errorCode: SentryErrorCode;
  expected?: boolean;
  failurePhase: SentryFailurePhase;
}

interface OperationHandle {
  reportFailure: (error: unknown, failure: FailureInput) => void;
}

const SERVICE = "xan-business";
const SOURCE_MAPPABLE_FRAME_RE =
  /^\s*at\s+(?:[A-Za-z0-9_$<>.]+\s+\()?(?:(?:https?:\/\/[A-Za-z0-9.-]+)?\/assets\/[-A-Za-z0-9_./@]+|[-A-Za-z0-9_./@]+):\d+:\d+\)?$/;

function durationMs(startedAt: number): number {
  return Math.round(performance.now() - startedAt);
}

/** Preserve a useful mapped stack while ensuring exception text cannot contain business data. */
function sanitizeException(error: unknown, operation: SentryOperation): Error {
  const message = `${operation} failed`;
  const sanitized = new Error(message);
  if (!(error instanceof Error)) return sanitized;

  const frames = (error.stack?.split("\n") ?? []).filter((line) =>
    SOURCE_MAPPABLE_FRAME_RE.test(line),
  );
  if (frames.length > 0) {
    sanitized.stack = [`Error: ${message}`, ...frames].join("\n");
  }
  return sanitized;
}

/**
 * Runs one user-visible operation inside a trace span. Only controlled tags
 * are indexed; opaque correlation and timing stay in structured context.
 */
export async function startSentryOperation<T>(
  input: OperationInput,
  work: (handle: OperationHandle) => Promise<T>,
): Promise<T> {
  const startedAt = performance.now();
  return startSpan(
    {
      name: input.operation,
      op: "ui.action",
      attributes: {
        operation: input.operation,
        request_id: input.requestId,
        service: SERVICE,
        ...(input.model ? { model: input.model } : {}),
        ...(input.provider ? { provider: input.provider } : {}),
      },
    },
    async (span) => {
      let completed = false;
      const complete = (outcome: "success" | "failure" | "expected") => {
        if (completed) return;
        completed = true;
        span.setAttribute("duration_ms", durationMs(startedAt));
        span.setAttribute("outcome", outcome);
      };

      const reportFailure = (error: unknown, failure: FailureInput) => {
        const duration = durationMs(startedAt);
        if (failure.failurePhase !== "post_commit") {
          if (completed) return;
          complete(failure.expected ? "expected" : "failure");
          span.setAttribute("error_code", failure.errorCode);
          span.setAttribute("failure_phase", failure.failurePhase);
        }
        if (failure.expected) return;

        captureException(sanitizeException(error, input.operation), {
          contexts: {
            xan_operation: {
              duration_ms: duration,
              ...(input.model ? { model: input.model } : {}),
              operation: input.operation,
              ...(input.provider ? { provider: input.provider } : {}),
              request_id: input.requestId,
              service: SERVICE,
            },
          },
          tags: {
            error_code: failure.errorCode,
            failure_phase: failure.failurePhase,
            operation: input.operation,
            outcome: "failure",
            service: SERVICE,
          },
        });
      };

      try {
        const result = await work({ reportFailure });
        complete("success");
        return result;
      } catch (error) {
        reportFailure(error, { errorCode: "request_failed", failurePhase: "request" });
        throw error;
      }
    },
  );
}
